import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { usageAPI } from '../lib/api';

const UsageContext = createContext(null);

// Una "sesión" para fines de tracking dura mientras el usuario no cierre la pestaña.
// El sessionId se guarda en sessionStorage (se borra al cerrar pestaña → cuenta como nueva navegación).
function getOrCreateSessionId() {
    try {
        let sid = window.sessionStorage.getItem('pdd_session_id');
        if (!sid) {
            sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
            window.sessionStorage.setItem('pdd_session_id', sid);
        }
        return sid;
    } catch (_) {
        return `s_${Date.now()}`;
    }
}

// Rutas internas que cuentan como "navegación" (NO contar /auth/*, ni /login, ni la landing /)
const INTERNAL_ROUTES_PREFIXES = [
    '/dashboard', '/chat', '/rubricas', '/progreso', '/perfil',
    '/borradores', '/tareas', '/admin', '/diagnostico', '/simulaciones',
];

export function UsageProvider({ children }) {
    const { user, isAuthenticated, loading: authLoading } = useAuth();
    const location = useLocation();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const trackedRef = useRef(false); // ya hicimos track-navigation en esta sesión

    const fetchStatus = useCallback(async () => {
        if (!isAuthenticated) {
            setStatus(null);
            return;
        }
        try {
            setLoading(true);
            const r = await usageAPI.getStatus();
            setStatus(r.data);
            return r.data;
        } catch (_) {
            // silencioso: no romper UI si /usage/status falla
            setStatus(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated]);

    // Cargar status al autenticarse
    useEffect(() => {
        if (!authLoading) fetchStatus();
    }, [authLoading, fetchStatus, user?.id]);

    // Track 1 vez por sesión cuando entra a una ruta interna.
    // El endpoint POST /api/usage/track-navigation YA devuelve el status completo,
    // así que sustituimos el state local con su respuesta para mostrar el banner
    // sin esperar a un nuevo fetch.
    useEffect(() => {
        if (!isAuthenticated || trackedRef.current) return;
        const path = location.pathname || '';
        const isInternal = INTERNAL_ROUTES_PREFIXES.some((p) => path === p || path.startsWith(p + '/'));
        if (!isInternal) return;
        trackedRef.current = true;
        (async () => {
            try {
                const sid = getOrCreateSessionId();
                const r = await usageAPI.trackNavigation(sid);
                setStatus(r.data);  // sincroniza inmediatamente
            } catch (_) {
                // silencioso
            }
        })();
    }, [isAuthenticated, location.pathname]);

    const value = {
        status,
        loading,
        refresh: fetchStatus,
        isAdmin: !!status?.is_admin,
        puedeUsarIA: !!status?.puede_usar_ia,
        navegacionesRestantes: status?.navegaciones_restantes ?? null,
        bloqueado: !!status?.bloqueado_navegacion,
        mensaje: status?.mensaje || null,
        mensajeIA: status?.mensaje_ia_bloqueada || null,
        planActivo: !!status?.plan_activo,
        planTipo: status?.plan_tipo || null,  // 'plataforma' | 'premium' | null
        // ───── Trial (período de exploración 7 días) ─────
        trial: status?.trial || null,
        trialExpirado: !!status?.trial?.expirado,
        trialDiaActual: status?.trial?.dia_actual ?? null,
        trialDiasRestantes: status?.trial?.dias_restantes ?? null,
        trialTotalDias: status?.trial?.total_dias ?? 7,
        trialPorcentaje: status?.trial?.porcentaje ?? 0,
        trialFechaFinHumano: status?.trial?.fecha_fin_humano ?? null,
        trialAccesoCompleto: status?.trial ? status.trial.acceso_completo : true,
        // ───── Asistente de Redacción · contador dedicado (Oct 2026) ─────
        improveWritingUnlimited: !!status?.improve_writing_unlimited,
        improveWritingCredits: status?.improve_writing_credits ?? null,
    };

    return <UsageContext.Provider value={value}>{children}</UsageContext.Provider>;
}

export function useUsage() {
    const ctx = useContext(UsageContext);
    if (!ctx) {
        // Devolver un objeto neutro: permite que componentes funcionen sin el provider (Landing/Login/Register)
        return {
            status: null, loading: false, refresh: () => {},
            isAdmin: false, puedeUsarIA: true, navegacionesRestantes: null,
            bloqueado: false, mensaje: null, mensajeIA: null, planActivo: false,
            planTipo: null,
            trial: null, trialExpirado: false, trialDiaActual: null,
            trialDiasRestantes: null, trialTotalDias: 7, trialPorcentaje: 0,
            trialFechaFinHumano: null,
            trialAccesoCompleto: true,
        };
    }
    return ctx;
}
